import { openDB, type DBSchema, type IDBPDatabase } from "idb";

/**
 * Client-side catalog + personalization stores (technical-spec §6.2). These
 * are caches of API data plus favorites/progress/profiles — the API stays the
 * source of truth. localStorage is reserved for tiny sync prefs (theme,
 * active profile id) per the spec.
 */
export const STORE_NAMES = [
  "channels",
  "programs",
  "epgCache",
  "vod",
  "series",
  "tmdbCache",
  "sources",
  "watchProgress",
  "favorites",
  "profiles",
  "channelHealth",
  "mirrors",
] as const;

export type StoreName = (typeof STORE_NAMES)[number];

interface SliqueDB extends DBSchema {
  channels: { key: string; value: unknown };
  programs: { key: string; value: unknown; indexes: { "by-channel": string } };
  epgCache: { key: string; value: unknown };
  vod: { key: string; value: unknown };
  series: { key: string; value: unknown };
  tmdbCache: { key: string; value: unknown; indexes: { "by-ts": number } };
  sources: { key: string; value: unknown };
  watchProgress: { key: string; value: unknown };
  favorites: { key: string; value: unknown };
  profiles: { key: string; value: unknown };
  channelHealth: { key: string; value: unknown };
  mirrors: { key: string; value: unknown };
}

const DB_NAME = "slique";
// Bump whenever STORE_NAMES changes: the upgrade handler only runs when the
// version increases, so a new store (e.g. channelHealth in Section 12) never
// appears on devices that already hold an older database at the same version
// — and writes to the missing store fail silently, killing the feature.
const DB_VERSION = 5;

let dbPromise: Promise<IDBPDatabase<SliqueDB>> | null = null;

export function openDb(): Promise<IDBPDatabase<SliqueDB>> {
  if (!dbPromise) {
    dbPromise = openDB<SliqueDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Indexed stores first (they carry their index in createObjectStore),
        // then the plain stores.
        if (!db.objectStoreNames.contains("programs")) {
          const programs = db.createObjectStore("programs");
          programs.createIndex("by-channel", "channelId");
        }
        if (!db.objectStoreNames.contains("tmdbCache")) {
          const cache = db.createObjectStore("tmdbCache");
          cache.createIndex("by-ts", "cachedAt");
        }
        for (const name of STORE_NAMES) {
          if (name === "programs" || name === "tmdbCache") continue;
          if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
        }
      },
    });
  }
  return dbPromise;
}

/** Generic put with an explicit key. */
export async function dbPut(store: StoreName, value: unknown, key: string): Promise<void> {
  const db = await openDb();
  await db.put(store, value, key);
}

/** Generic get by key. */
export async function dbGet<T = unknown>(store: StoreName, key: string): Promise<T | undefined> {
  const db = await openDb();
  return (await db.get(store, key)) as T | undefined;
}

/** Bulk put (upsert) — the normal path for catalog syncs. */
export async function dbBulkPut(store: StoreName, entries: Array<[string, unknown]>): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  await Promise.all(entries.map(([key, value]) => tx.store.put(value, key)));
  await tx.done;
}

/** Query all rows in a store. */
export async function dbAll<T = unknown>(store: StoreName): Promise<T[]> {
  const db = await openDb();
  return (await db.getAll(store)) as T[];
}

/** Query rows by an index on a store (e.g. programs by channelId). */
export async function dbAllByIndex<T = unknown>(
  store: StoreName,
  indexName: "by-channel" | "by-ts",
  value: string | number
): Promise<T[]> {
  const db = await openDb();
  // The union of store names widens the index param to `never`; use a typed
  // alias to keep the call site safe while satisfying the generic schema.
  const aliased = db as unknown as {
    getAllFromIndex(store: string, index: string, value: string | number): Promise<unknown[]>;
  };
  return (await aliased.getAllFromIndex(store, indexName, value)) as T[];
}

/** Every key in a store. */
export async function dbKeys(store: StoreName): Promise<string[]> {
  const db = await openDb();
  return (await db.getAllKeys(store)).map(String);
}

export async function dbDelete(store: StoreName, key: string): Promise<void> {
  const db = await openDb();
  await db.delete(store, key);
}

export async function dbClear(store: StoreName): Promise<void> {
  const db = await openDb();
  await db.clear(store);
}

/** Count rows (useful for cache-size telemetry). */
export async function dbCount(store: StoreName): Promise<number> {
  const db = await openDb();
  return db.count(store);
}
